<template>
    <button
        :class="classBuilder('ToggleButton')"
        :disabled="disabled"
        @click.stop="e => $emit('click', e)"
        @mousedown="active = true"
        @mouseup="active = false"
    >
        <slot>
            <StreamlineIcon
                v-if="showIcon"
                :class="classBuilder('ToggleButton__icon')"
                :icon="option.icon"
                :size="16"
                :stroke="primaryIconColor"
            />
            <span class="ToggleButton__text">{{ option.label }}</span>
        </slot>
        <StreamlineIcon
            v-if="!hideArrow"
            :class="classBuilder('ToggleButton__arrow-icon')"
            :stroke="arrowColor"
            :icon="arrowIcon"
            :size="12"
        />
    </button>
</template>
<script>
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {ArrowUp1, ArrowDown1} from "@reviewpush/streamline-regular/lib/arrows-diagrams/Arrows";
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import colors from 'style/_colors.scss';

export default {
    name: 'Button',
    components: {
        StreamlineIcon
    },
    props: {
        open: {
            type: Boolean,
            required: true,
        },
        option: {
            type: Object,
            required: true,
        },
        hideArrow: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        iconColor: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            default: 'primary',
            validator: val => ['primary', 'secondary', 'tertiary'].indexOf(val) !== -1,
        },
    },
    data(){
        return {
            active: false
        };
    },
    computed: {
        selectedOption(){
            return this.options.find( opt => opt.value === this.value);
        },
        showIcon(){
            return has(this.option, 'icon')  && !isEmpty(this.option.icon);
        },
        primaryIconColor(){
            if(this.disabled) {
                return colors.grey100;
            }
            return this.active
                ? colors.primary
                : this.iconColor;
        },
        arrowIcon(){
            return this.open
                ? ArrowUp1
                : ArrowDown1;
        },
        arrowColor(){
            return (this.disabled)
                ? colors.grey100
                : colors.textDark;
        },
    },
    methods: {
        classBuilder(baseClass){
            return`${baseClass} ${baseClass}--${this.type}`;
        },
    }
};
</script>
<style lang="scss">
    .ToggleButton {
        min-height: 1.5rem;
        font-family: proxima-nova, sans-serif;
        padding: 0 0.5rem;
        font-size: 1rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border: 1px solid transparent;
        border-radius: 4px;
        background: $white;
        color: $text-dark;
        outline: none;
        cursor: pointer;

        &:disabled {
            cursor: default;
            color: $grey-100;
        }

        &--primary {
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
            &:hover { box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15); }
            &:focus { box-shadow: 0 0 4px $primary; }
            &:active {
                outline: none;
                box-shadow: none;
                border-color: $primary;
            }
            &:disabled {
                box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
                border: 1px solid transparent;
            }
        }
        &--secondary {
            border-color: $grey;
            &:active {
                outline: none;
                box-shadow: none;
                border-color: $primary;
            }
            &:disabled {
                border-color: $grey-100;
                color: $grey-100;
            }
        }
        &--tertiary {
            &:active { color: $primary; }
            &:disabled {
                border: none;
                color: $grey-100;
            }
        }

        &__icon {
            height: 1rem;
            width: 1rem;
            margin: auto 0.25rem auto 0;
        }
        &__arrow-icon {
            margin: auto 0 auto 0.5rem;
        }
    }
</style>
